import { Component, NgZone } from "@angular/core";
import { DomSanitizer, Title } from "@angular/platform-browser";
import { NavigationEnd, Router } from "@angular/router";
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from "@capacitor/status-bar";
import { AlertController, LoadingController, MenuController, ModalController, NavController, Platform, PopoverController, ToastController } from "@ionic/angular";
import { AppSettings } from "./core/configs/app.settings";
import { Entity } from "./core/data/schema/entity";
import { EntityFactory } from "./core/data/schema/entity-factory";
import { HttpService } from "./core/data/service/http.service";
import { ServiceHelper } from "./core/helper/services.helper";
import { AuthenticationService } from "./core/modules/auth-core/data/service/authentication.service";
import { AuctionEventType, IAuctionEvent } from "./data/schema/interfaces";
import { PushMessage } from "./data/schema/push-message";
import { AuctionService } from "./data/service/auction.service";
import { ConfigService } from "./data/service/config.service";
import { MessagesService } from "./data/service/messages.service";
import { OneSignalService } from "./data/service/onesignal.service";
import { register } from 'swiper/element/bundle';
import { Capacitor } from "@capacitor/core";

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent {

  ticketingEnabled: boolean = false;

  // set in home.ts from file loaded from app server
  systemMessage: string;

  private itemAlert: HTMLIonAlertElement;

  helpRoute: string;

  constructor(
    private router: Router,
    private platform: Platform,
    private auctionService: AuctionService,
    private zone: NgZone,
    private configService: ConfigService,
    private authSvc: AuthenticationService,
    private alertCtrl: AlertController,
    private navCtrl: NavController,
    private toastCtrl: ToastController,
    private messageService: MessagesService,
    private onesignalSvc: OneSignalService,
    modalCtrl: ModalController,
    title: Title,
    popoverCtrl: PopoverController,
    menuCtrl: MenuController,
    sanitizer: DomSanitizer,
    loadCtrl: LoadingController,
    httpService: HttpService,
  ) {

    Entity.factory = new EntityFactory(httpService);

    ServiceHelper.authSvc = authSvc;
    ServiceHelper.popoverCtrl = popoverCtrl;
    ServiceHelper.loadCtrl = loadCtrl;
    ServiceHelper.navCtrl = navCtrl;
    ServiceHelper.platform = platform;
    ServiceHelper.alertCtrl = alertCtrl;
    ServiceHelper.toastCtrl = toastCtrl;
    ServiceHelper.httpSvc = httpService;
    ServiceHelper.title = title;
    ServiceHelper.menuCtrl = menuCtrl;
    ServiceHelper.sanitizer = sanitizer;
    ServiceHelper.modalCtrl = modalCtrl;
    ServiceHelper.appComponent = this;

    this.initializeApp();

    this.router.events.forEach(item => {
      if (item instanceof NavigationEnd) {
        this.updateHelpBubble(item);
      }
    });

    // Bind to the back button
    this.platform.backButton.subscribeWithPriority(10, () => {
      window.history.back();
    });


    // clean up any old error logs (Remove next time you see this as it happened on 2025-08-20)
    localStorage.removeItem('error-log');
  }

  updateHelpBubble(item: any) {
    var pageKey = this.getUrl(item.url);
    // console.log('Page Key:', pageKey);
    this.helpRoute = pageKey;
  }

  getUrl(page: string) {
    let url = page.replace('/auction/', '').replace(/[0-9]/g, '');
    url = page.replace('/manage/', '').replace(/[0-9]/g, '');
    const slashIndex = url.indexOf('/');
    if (slashIndex > 0) {
      url = url.substring(slashIndex);
    }
    return url;
  }

  async initializeApp() {
    // Setup the menu
    this.bindEvents();
    this.initOneSignal();

    // Platform stuff
    if (Capacitor.isNativePlatform()) {
      // Set the status bar style
      await StatusBar.setStyle({ style: Style.Light });
      await SplashScreen.hide();
    }
    // resuming from background
    this.platform.resume.subscribe(() => {
      if (this.auctionService.inAuction) {
        this.auctionService.reloadAuction().catch(err => {
          console.error('Error reloading auction after resume', err);
          this.router.navigateByUrl('/home');
        });
      }
    });

    if (Capacitor.isNativePlatform()) {
      App.addListener('appUrlOpen', (data: URLOpenListenerEvent) => {
        this.handleDeepLink(data.url);
      });

      const launchUrl = await App.getLaunchUrl();
      if (launchUrl?.url) {
        this.handleDeepLink(launchUrl.url);
      }
    }

    // check for system message
    this.checkForSysMessage();
  }

  private handleDeepLink(rawUrl: string): void {
    this.zone.run(() => {
      const url = new URL(rawUrl);
      const path = url.pathname;

      if (path.includes('share/auction.php')) {
        const code = url.searchParams.get('code');
        if (code) {
          this.router.navigateByUrl(`/auction/${code}/details`);
        }
      } else if (path.includes('share/item.php')) {
        const code = url.searchParams.get('code');
        const item = url.searchParams.get('item');
        if (code && item) {
          this.router.navigateByUrl(`/auction/${code}/item/${item}`);
        }
      } else if (url.hash) {
        this.router.navigateByUrl(url.hash.replace(/^#/, ''));
      }
    });
  }

  private async checkForSysMessage() {

    try {
      const versionCheckResp = <any>await this.configService.messageCheck();
      // console.log('Version Check Response at ' + new Date(), versionCheckResp);
      // console.log('  this build: ', AppSettings.build);


      let msg = "";
      let msgs = versionCheckResp[AppSettings.build];
      if (msgs != null) {
        if (this.platform.is('ios') && msgs.ios != null && msgs.ios.length > 1) {
          msg = msgs.ios;
        } else if (this.platform.is('android') && msgs.ios != null && msgs.ios.length > 1) {
          msg = msgs.android;
        } else if (!this.platform.is('ios') && !this.platform.is('android') && msgs.web != null && msgs.web.length > 1) {
          msg = msgs.web;
        } else {
          msg = msgs.global;
        }
      }
      if (msg != null && msg.length > 0) {
        this.systemMessage = msg;

        // check every 15 seconds to remove
        setTimeout(() => {
          this.checkForSysMessage()
        }, 15000);
      } else {
        this.systemMessage = null;
        // check every 15 minutes for new message
        setTimeout(() => {
          this.checkForSysMessage()
        }, 60000 * 15);
      }
    } catch (error) {
      console.error(error);
    }

  }

  get displayName() { return (this.authSvc.profile.displayName || ''); }
  get isAuctionAdmin() { return this.auctionService.isAuctionAdmin; }
  get version() { return AppSettings.version; }

  logout() {
    this.authSvc.logout();
    this.navCtrl.navigateRoot('/auth/login');
  }

  async promptForLeaveAuction() {
    this.alertCtrl.create({
      header: 'Abandon This Auction?',
      message:
        `Abandoning the auction means you the <strong>auction will be removed from your list</strong> and you will no longer be able to participate.
        <br>You will need to re-join the auction in order to place bids. Any payments made will not be refunded by Bid Beacon.`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Abandon Auction',
          handler: () => { this.leaveAuction(); }
        }
      ]
    }).then(m => m.present());
  }

  private bindEvents() {
    // Now bind to the auction events
    this.auctionService.auctionEvent.subscribe(async event => {
      const auctionEvent = event as IAuctionEvent;
      if (auctionEvent.event === AuctionEventType.AuctionUpdated) {
      } else if (auctionEvent.event === AuctionEventType.NewBroadcast) {
        const pushMessage = new PushMessage(auctionEvent.payload);
        if (pushMessage.productId != null) {
          this.itemAlert = await this.displayPrompt('Auction Notification', pushMessage.message, pushMessage.productId);
        } else {
          this.displayAlert('Auction Notification', pushMessage.message);
        }
      } else if (auctionEvent.event === AuctionEventType.NewMessage) {
        // If it was a new message, then show a toast with the message
        const msgToast = await this.toastCtrl.create({
          message: auctionEvent.payload.message,
          duration: 4000,
          buttons: [{ text: 'View', role: 'cancel' }]
        });
        msgToast.present();
        const threadUrl = `/auction/${this.auctionService.currentAuction.accessCode}/message/thread/${auctionEvent.id}`;
        // if we aren't on the message page, then increment the unread count
        if (this.router.routerState.snapshot.url.indexOf(threadUrl) < 0) {
          console.log('Incrementing unread count');
          this.messageService.unreadMessageCount++;
        }

        const toastResult = await msgToast.onDidDismiss();
        if (toastResult.role === 'cancel') {
          this.navCtrl.navigateForward(threadUrl);
        }
      } else if (auctionEvent.event === AuctionEventType.ItemAdded || auctionEvent.event === AuctionEventType.WinStateChanged) {
        // Wait for other pop-ups to be dismissed
        if (this.itemAlert != null) {
          await this.itemAlert.dismiss();
        }
        // If it's not a new item, or we are not admin.. show an alert
        if (auctionEvent.event !== AuctionEventType.ItemAdded || this.isAuctionAdmin === false) {
          const payload = auctionEvent.payload;
          this.itemAlert = await this.displayPrompt(payload.title, payload.message, payload.item);
        }
      } else if (auctionEvent.event === AuctionEventType.AuctionEndChanged) {
        if (auctionEvent.payload != null) {
          const msgToast = await this.toastCtrl.create({
            message: auctionEvent.payload,
            duration: 4000,
            buttons: [{ text: 'View', role: 'cancel' }]
          });
          msgToast.present();
          const toastResult = await msgToast.onDidDismiss();
          if (toastResult.role === 'cancel') {
            this.navCtrl.navigateForward(`/auction/${this.auctionService.currentAuction.accessCode}/item/${auctionEvent.id}`);
          }
        }
      }
    });
  }

  private initOneSignal() {
    this.onesignalSvc.pushHandler.subscribe((data) => {
      console.log('Onesignal event with data: ', data);
      if (data.type.toLowerCase() === 'product') {
        const url = `/auction/${data.accessCode}/item/${data.id}`;
        this.navCtrl.navigateForward(url);
      } else if (data.type.toLowerCase() === 'message') {
        this.navCtrl.navigateForward(`/auction/${data.accessCode}/message/thread/${data.id}`);
      }
    });
  }

  private async displayAlert(header: string, message: string) {
    const alert = await this.alertCtrl.create({
      header,
      message,
      buttons: ['OK']
    });

    alert.present();
    return alert;
  }

  private async displayPrompt(header: string, message: string, id: number) {
    const alert = await this.alertCtrl.create({
      header,
      message,
      buttons: [
        {
          text: 'Dismiss',
          role: 'cancel'
        },
        {
          text: 'View',
          handler: () => {
            this.navCtrl.navigateForward(`/auction/${this.auctionService.currentAuction.accessCode}/item/${id}`);
          }
        }
      ]
    });
    alert.present();
    alert.onDidDismiss().then(() => { this.itemAlert = null; });
    return alert;
  }

  private async leaveAuction() {
    try {
      await this.auctionService.leaveAuction(this.auctionService.currentAuction.auctionId);
    } catch (error) {
      console.error('Error leaving auction', error);
      this.displayAlert('Leave Error', error);
    }
  }


}
